import { DestroyRef, inject, Injectable, TemplateRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AdvertisingModalContentModel } from '@libs/cms/models';
import { CapacitorService, DialogService } from '@libs/shared/common';
import { CHECKOUT_CART_ROUTE_PATH, CHECKOUT_OVERVIEW_ROUTE_PATH, URL_PARAMETER_UTM_MEDIUM } from '@libs/shared/models';
import { UrlParameterService } from '@libs/shared/services';
import { TrackingGoogleService } from '@libs/shared/tracking';
import { map, of, Subscription, switchMap } from 'rxjs';
import { AdvertisingDialogResult, AdvertisingModalState } from './advertising-modal-state.service';

interface AdvertisingModalRegistration {
  modal: AdvertisingModalContentModel;
  templateRef: TemplateRef<void>;
}

@Injectable()
export class AdvertisingModalService {
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly dialog: DialogService = inject(DialogService);
  private readonly state: AdvertisingModalState = inject(AdvertisingModalState);
  private readonly tracking: TrackingGoogleService = inject(TrackingGoogleService);
  private readonly capacitor: CapacitorService = inject(CapacitorService);
  private readonly urlParameterService: UrlParameterService = inject(UrlParameterService);
  private readonly router: Router = inject(Router);

  private isOpen: boolean = false;
  private showLaterSubscription?: Subscription;

  public registerModal(modal: AdvertisingModalContentModel, templateRef: TemplateRef<void>): void {
    this.checkAndOpenModal({ modal, templateRef });
  }

  private checkAndOpenModal(registration: AdvertisingModalRegistration): void {
    if (this.urlParameterService.has(URL_PARAMETER_UTM_MEDIUM)) {
      return;
    }

    if (this.isModalBlockedForCurrentRoute()) {
      return;
    }

    if (!this.isOpen && this.state.shouldShow(registration.modal)) {
      this.showLaterSubscription?.unsubscribe();
      this.show(registration);
    }
  }

  private isModalBlockedForCurrentRoute(): boolean {
    const currentUrl: string = this.router.url;

    return [
      `/${CHECKOUT_CART_ROUTE_PATH}`,
      `/${CHECKOUT_OVERVIEW_ROUTE_PATH}`,
    ].some((route: string): boolean => currentUrl.startsWith(route));
  }

  private show(registration: AdvertisingModalRegistration): void {
    this.isOpen = true;

    this.dialog.queueDialog<void, unknown, AdvertisingDialogResult>(registration.templateRef, {
      autoFocus: 'dialog',
      disableClose: true,
    }).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(dialogRef => {
        if (!dialogRef) {
          return of(undefined);
        }

        if (registration.modal.promotion) {
          this.tracking.trackPageBlockImpressionEvent(registration.modal.promotion);
        }

        return dialogRef.afterClosed();
      }),
      map(userAction => void this.afterClosed(registration, userAction)),
    ).subscribe();
  }

  private afterClosed(registration: AdvertisingModalRegistration, action?: AdvertisingDialogResult): void {
    this.isOpen = false;

    if (!action) {
      return;
    }

    this.state.saveResult(registration.modal, action);

    if (registration.modal.promotion) {
      this.tracking.trackPageBlockClickEvent({ ...registration.modal.promotion, ...{ click_type: action } });
    }

    if (action === 'later') {
      this.subscribeShowLater(registration);
    }
  }

  private subscribeShowLater(registration: AdvertisingModalRegistration): void {
    this.showLaterSubscription = this.capacitor.resume$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.checkAndOpenModal(registration);
      });
  }
}
